import { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import {
  RETORNABLES_MOVIMIENTOS_QUERY,
  RETORNABLES_SALDOS_QUERY,
  RETORNABLES_TIPOS_QUERY,
} from '@/graphql/retornables.gql.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
};

export default function BolsinesPorCliente() {
  const { sucursal } = useAuth();
  const sucursalId = Number(sucursal?.id || 1);
  const [clienteId, setClienteId] = useState('');

  const variables = useMemo(() => ({
    sucursalId,
    clienteId: clienteId ? Number(clienteId) : null,
  }), [clienteId, sucursalId]);

  const [tiposResult] = useQuery({ query: RETORNABLES_TIPOS_QUERY, variables: { activo: true } });
  const [saldosResult] = useQuery({ query: RETORNABLES_SALDOS_QUERY, variables });
  const [movResult] = useQuery({ query: RETORNABLES_MOVIMIENTOS_QUERY, variables: { filtro: variables } });

  const tiposMap = useMemo(
    () => new Map((tiposResult.data?.retornablesTipos || []).map((item) => [Number(item.id), item.nombre])),
    [tiposResult.data],
  );
  const saldos = saldosResult.data?.retornablesSaldos || [];
  const movimientos = movResult.data?.retornablesMovimientos || [];

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 px-4 py-4">
      <header className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgb(var(--panel))] p-4">
        <h1 className="text-2xl font-semibold text-[rgb(var(--fg))]">Bolsines por Cliente</h1>
        <p className="mt-1 text-sm text-[rgba(var(--fg),0.7)]">
          Consulta mínima de retornables/documentación física por cliente.
        </p>
      </header>

      <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgb(var(--panel))] p-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-3">
            <label className="text-sm font-medium text-[rgb(var(--fg))]">Sucursal</label>
            <input value={sucursalId} disabled className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.6)] px-3 py-2 text-sm" />
          </div>
          <div className="col-span-12 md:col-span-4">
            <label className="text-sm font-medium text-[rgb(var(--fg))]">Cliente ID</label>
            <input value={clienteId} onChange={(event) => setClienteId(event.target.value)} placeholder="Filtrar por cliente" className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2 text-sm" />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgb(var(--panel))] p-4">
        <h2 className="mb-3 text-lg font-semibold text-[rgb(var(--fg))]">Saldos abiertos</h2>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[rgba(var(--border),0.45)] text-left"><th className="px-3 py-2">Tipo</th><th className="px-3 py-2">Cliente</th><th className="px-3 py-2 text-right">Saldo</th></tr></thead>
            <tbody>
              {saldos.length ? saldos.map((row, index) => (
                <tr key={`${row.tipoId}-${row.clienteId || 'sin-cliente'}-${index}`} className="border-b border-[rgba(var(--border),0.3)]">
                  <td className="px-3 py-2">{tiposMap.get(Number(row.tipoId)) || `Tipo #${row.tipoId}`}</td>
                  <td className="px-3 py-2">{row.clienteId || '-'}</td>
                  <td className="px-3 py-2 text-right font-semibold">{Number(row.saldo || 0)}</td>
                </tr>
              )) : (
                <tr><td colSpan={3} className="px-3 py-4 text-center text-[rgba(var(--fg),0.6)]">No hay saldos de bolsines para el filtro actual.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgb(var(--panel))] p-4">
        <h2 className="mb-3 text-lg font-semibold text-[rgb(var(--fg))]">Trazabilidad</h2>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[rgba(var(--border),0.45)] text-left"><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Tipo</th><th className="px-3 py-2">Movimiento</th><th className="px-3 py-2">Cliente</th><th className="px-3 py-2">Guía/Foja/Reparto</th><th className="px-3 py-2 text-right">Cant.</th><th className="px-3 py-2">Estado</th></tr></thead>
            <tbody>
              {movimientos.length ? movimientos.map((row) => (
                <tr key={row.id} className="border-b border-[rgba(var(--border),0.3)]">
                  <td className="px-3 py-2">{formatDateTime(row.fecha)}</td>
                  <td className="px-3 py-2">{tiposMap.get(Number(row.tipoId)) || `Tipo #${row.tipoId}`}</td>
                  <td className="px-3 py-2">{row.movimiento}</td>
                  <td className="px-3 py-2">{row.clienteId || '-'}</td>
                  <td className="px-3 py-2">{row.guiaId ? `Guía #${row.guiaId}` : row.fojaId ? `Foja #${row.fojaId}` : row.repartoId ? `Reparto #${row.repartoId}` : '-'}</td>
                  <td className="px-3 py-2 text-right">{row.cantidad}</td>
                  <td className="px-3 py-2">{row.estado}</td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="px-3 py-4 text-center text-[rgba(var(--fg),0.6)]">No hay movimientos para el filtro actual.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
